import {
	clientGetStreamingServiceTrackInformation,
	clientPromptSearch,
	clientSearchByAudio,
} from '@/client'

import { create } from 'zustand'

interface AiSearchState {
	aiSearchResults: number[]
	aiSearchFilters: string
	blockAiSearch: boolean
	clearAiSearch: () => void
	aiSearchHasFailed: boolean
	aiSearchPending: boolean
	setAiSearchPending: (boolean: boolean) => void

	prefetchedSearchByAudio: {
		service: string
		title: string
		url: string
		aiSearchResults: number[] | undefined
		aiSearchFilters: string | undefined
	}
	searchByAudio: {
		service: string
		title: string
		url: string
	}
	prefetchSearchByAudio: (url: string, isLoggedIn: boolean) => void
	removePrefetchedSearchByAudio: () => void
	setSearchByAudio: (url?: string) => void

	searchByPrompt: string
	setSearchByPrompt: (text: string) => void
}

const useAiSearchStore = create<AiSearchState>()((set, get) => ({
	aiSearchFilters: null,
	aiSearchResults: null,
	blockAiSearch: false,
	aiSearchHasFailed: false,
	clearAiSearch: () => {
		set(() => ({
			aiSearchFilters: null,
			aiSearchResults: null,
			searchByAudio: null,
			searchByPrompt: null,
			prefetchedSearchByAudio: null,
			aiSearchHasFailed: false,
		}))
	},

	aiSearchPending: false,
	setAiSearchPending: (boolean) => {
		set(() => ({
			aiSearchPending: boolean,
		}))
	},

	prefetchedSearchByAudio: null,
	searchByAudio: null,
	prefetchSearchByAudio: async (url, isLoggedIn) => {
		if (url === get().searchByAudio?.url) {
			return
		}

		get().clearAiSearch()

		const { service, title } =
			(await clientGetStreamingServiceTrackInformation(url)) ?? {}

		if (service && title) {
			set(() => ({
				blockAiSearch: !isLoggedIn,
				prefetchedSearchByAudio: {
					service,
					title,
					url,
					aiSearchResults: [],
					aiSearchFilters: '',
				},
			}))

			if (!isLoggedIn) {
				return
			}

			const ids = await clientSearchByAudio(url)

			set((state) => ({
				prefetchedSearchByAudio: {
					...state.prefetchedSearchByAudio,
					aiSearchResults: ids?.map((id) => parseInt(id, 10)),
					aiSearchFilters: `${ids?.map((id) => `Id = ${id}`).join(' OR ')}`,
				},
			}))
		}
	},
	removePrefetchedSearchByAudio: () => {
		set(() => ({
			prefetchedSearchByAudio: {
				service: null,
				title: null,
				url: null,
				aiSearchResults: null,
				aiSearchFilters: null,
			},
		}))
	},
	setSearchByAudio: async (input) => {
		set(() => ({
			aiSearchPending: true,
		}))

		const waitForPrefetch = setInterval(() => {
			const { blockAiSearch, prefetchedSearchByAudio } = get()

			if (
				(input === prefetchedSearchByAudio?.url ||
					input === prefetchedSearchByAudio?.title) &&
				(prefetchedSearchByAudio?.aiSearchFilters || blockAiSearch)
			) {
				clearInterval(waitForPrefetch)

				set(() => ({
					searchByAudio: {
						service: prefetchedSearchByAudio.service,
						title: prefetchedSearchByAudio.title,
						url: prefetchedSearchByAudio.url,
					},
					aiSearchFilters: prefetchedSearchByAudio.aiSearchFilters,
					aiSearchResults: prefetchedSearchByAudio.aiSearchResults,
					aiSearchPending: false,
				}))
			}
		}, 250)
	},

	searchByPrompt: null,
	setSearchByPrompt: async (text) => {
		if (text === get().searchByPrompt) {
			return
		}

		//Dont prompt search urls
		if (text.match(/(http|https|www)/i) !== null) {
			//Give time for algolia to start searching, else no results pops up
			setTimeout(() => {
				set(() => ({
					aiSearchPending: false,
				}))
			})
			return
		}
		get().clearAiSearch()

		set(() => ({
			aiSearchPending: true,
			aiSearchHasFailed: false,
		}))

		set(() => ({
			searchByPrompt: text,
		}))

		const ids = await clientPromptSearch(text)

		if (ids.length === 0) {
			set(() => ({
				aiSearchResults: null,
				aiSearchFilters: '',
				blockAiSearch: false,
				aiSearchHasFailed: true,
				aiSearchPending: false,
			}))

			return
		}

		set(() => ({
			aiSearchResults: ids?.map((id) => parseInt(id, 10)),
			aiSearchFilters:
				ids?.length > 0 ? `${ids?.map((id) => `Id = ${id}`).join(' OR ')}` : '',
			blockAiSearch: false,
		}))

		//Give time for algolia to start searching, else no results pops up
		setTimeout(() => {
			set(() => ({
				aiSearchPending: false,
			}))
		})
	},
}))

export default useAiSearchStore
